import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LANDING } from "@/constants/testIds";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const stats = [
  { k: "$44B", l: "India Space Economy by 2033" },
  { k: "200K+", l: "New Space-Sector Jobs (Adecco)" },
  { k: "250+", l: "Space Startups in India, 2025" },
  { k: "42.6%", l: "Graduate Employability (Mercer 2025)" },
];

const personas = [
  {
    id: "individual",
    code: "01",
    title: "Individuals",
    tag: "Students · Professionals · Career Switchers",
    body: "Diagnose your career forte across technical, commercial, regulatory, funding, insurance and sustainability dimensions. Get an AI-authored role map for India's space economy.",
    test: LANDING.personaIndividual,
  },
  {
    id: "startup",
    code: "02",
    title: "Startups",
    tag: "0–50 Employees · Pre-seed to Series B",
    body: "Score your startup on six readiness axes. Ship an investor-ready pitch, insurance-ready risk map, and IN-SPACe-aware regulatory checklist.",
    test: LANDING.personaStartup,
  },
  {
    id: "corporate",
    code: "03",
    title: "Corporates",
    tag: "Telecom · BFSI · Manufacturing · Defense",
    body: "Map existing capabilities onto space adjacencies. Upskill workforce, target sub-sectors, and connect with startups and regulators — with executive-grade analytics.",
    test: LANDING.personaCorporate,
  },
  {
    id: "investor",
    code: "04",
    title: "Investors",
    tag: "VC · PE · Family Offices · Corporate Ventures",
    body: "Access a curated, Forte²-scored pipeline. Underwrite mission, regulatory, and insurance risk with Orbix-powered intelligence.",
    test: LANDING.personaInvestor,
  },
];

const values = [
  ["Space-sector specialisation", "Sector-agnostic", "India-first"],
  ["Multi-dimensional readiness scoring", "Single-metric employability", "6 dimensions per persona"],
  ["AI-authored report", "Static or none", "Personalised AI report"],
  ["Regulatory & insurance guidance", "Not covered", "Orbix-integrated"],
  ["Talent marketplace", "Generic job boards", "Space-curated with mission-phase filters"],
  ["Investor & corporate tools", "Absent", "Full suite"],
  ["Geographic focus", "Global / EU", "India-first"],
];

export default function Landing() {
  const [wl, setWl] = useState({ email: "", name: "", persona: "individual" });
  const [status, setStatus] = useState("");

  const submitWaitlist = async (e) => {
    e.preventDefault();
    setStatus("");
    try {
      await api.waitlist(wl);
      setStatus("You're on the manifest. We'll be in touch.");
      setWl({ email: "", name: "", persona: "individual" });
    } catch {
      setStatus("Could not submit. Try again.");
    }
  };

  return (
    <div className="bg-[color:var(--f2-bg)] text-white min-h-screen">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden vignette">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 relative z-10">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-8">
              <div className="flex items-center gap-3 mb-8">
                <span className="live-dot" />
                <span className="label-xs">Live · India · Forte² Score v0.9</span>
              </div>
              <h1 className="font-display font-black tracking-tighter uppercase text-[44px] sm:text-6xl lg:text-[84px] leading-[0.95]">
                Find your <span className="saffron">forte</span><br />
                in India's <span className="saffron">space</span> economy.
              </h1>
              <p className="mt-8 max-w-2xl text-zinc-300 text-base leading-relaxed" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.85)" }}>
                Forte² Space is India&apos;s first career-readiness and capability-mapping platform for the space economy —
                for individuals, startups, corporates and investors. Diagnostics, learning pathways, mentorship and a
                talent marketplace, aligned to IN-SPACe&apos;s 2033 decadal vision.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/assessment" data-testid={LANDING.heroCta} className="btn-crisp btn-primary">Take the Forte² Score →</Link>
                <a href="#personas" data-testid={LANDING.heroSecondary} className="btn-crisp">Explore Personas</a>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-4 relative">
              <div className="hard-border p-6 bg-[color:var(--f2-surface)]">
                <div className="label-xs mb-4">Mission Brief · 2033</div>
                <div className="space-y-4">
                  {stats.map((s) => (
                    <div key={s.l} className="flex justify-between items-baseline border-b border-[color:var(--f2-border)] pb-3">
                      <span className="font-display text-3xl font-black tracking-tight saffron">{s.k}</span>
                      <span className="label-xs text-right max-w-[60%]">{s.l}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-xs text-[color:var(--f2-muted)] leading-relaxed">
                  Sources: SIA-India, IN-SPACe, Adecco 2025, Mercer Graduate Skill Index 2025.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <section className="border-y border-[color:var(--f2-border)] py-4 overflow-hidden">
        <div className="ticker label-xs">
          {[...Array(2)].flatMap((_, i) => [
            "IN-SPACe Decadal Vision · 2033",
            "US$9B → US$44B",
            "2% → 8% Global Share",
            "250+ Indian Space Startups",
            "72% Report Software/Data Skill Gaps",
            "Only 42.6% Graduates Job-Ready",
            "Orbital Saffron · Since 2026",
          ].map((t, j) => <span key={`${i}-${j}`}>◆ {t}</span>))}
        </div>
      </section>

      {/* PERSONAS */}
      <section id="personas" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-xs mb-2">§ 02 — Personas</div>
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl uppercase">
              Four flight paths.<br /><span className="saffron">One platform.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {personas.map((p) => (
            <Link
              to={`/for/${p.id}`}
              key={p.id}
              data-testid={p.test}
              className="hard-border p-8 bg-[color:var(--f2-surface)] hover:border-[color:var(--f2-saffron)] transition-colors group"
            >
              <div className="flex items-baseline justify-between mb-6">
                <span className="label-xs saffron">{p.code}</span>
                <span className="label-xs">{p.tag}</span>
              </div>
              <h3 className="font-display uppercase text-3xl font-black tracking-tight mb-4 group-hover:text-[color:var(--f2-saffron)] transition-colors">
                {p.title}
              </h3>
              <p className="text-sm text-[color:var(--f2-muted)] leading-relaxed">{p.body}</p>
              <div className="mt-6 label-xs group-hover:saffron">Enter →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* VALUE CURVE */}
      <section className="border-t border-[color:var(--f2-border)] bg-[color:var(--f2-surface)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="label-xs mb-2">§ 03 — Value Curve</div>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl uppercase mb-12">
            Why Forte² Space is different.
          </h2>
          <div className="hard-border">
            <div className="grid grid-cols-3 label-xs border-b border-[color:var(--f2-border)]">
              <div className="p-4">Capability</div>
              <div className="p-4 border-l border-[color:var(--f2-border)]">Category Standard</div>
              <div className="p-4 border-l border-[color:var(--f2-border)] saffron">Forte² Space</div>
            </div>
            {values.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 text-sm ${i > 0 ? "border-t border-[color:var(--f2-border)]" : ""}`}>
                <div className="p-4">{row[0]}</div>
                <div className="p-4 border-l border-[color:var(--f2-border)] text-[color:var(--f2-muted)]">{row[1]}</div>
                <div className="p-4 border-l border-[color:var(--f2-border)] saffron">{row[2]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <div className="label-xs mb-2">§ 04 — Manifest</div>
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl uppercase">
              Join the <span className="saffron">launch manifest.</span>
            </h2>
            <p className="mt-4 text-[color:var(--f2-muted)] max-w-md leading-relaxed">
              Early cohorts get discounted Forte² Scores, mentor introductions and IN-SPACe partner briefings.
            </p>
          </div>
          <form onSubmit={submitWaitlist} className="col-span-12 md:col-span-7 hard-border p-8 bg-[color:var(--f2-surface)] space-y-4">
            <div>
              <label className="label-xs block mb-2">Full Name</label>
              <input
                data-testid={LANDING.waitlistName}
                className="input-crisp"
                value={wl.name}
                onChange={(e) => setWl({ ...wl, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Email</label>
              <input
                data-testid={LANDING.waitlistEmail}
                type="email"
                className="input-crisp"
                value={wl.email}
                onChange={(e) => setWl({ ...wl, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Persona</label>
              <select
                data-testid={LANDING.waitlistPersona}
                className="input-crisp"
                value={wl.persona}
                onChange={(e) => setWl({ ...wl, persona: e.target.value })}
              >
                <option value="individual">Individual</option>
                <option value="startup">Startup</option>
                <option value="corporate">Corporate</option>
                <option value="investor">Investor</option>
              </select>
            </div>
            <button data-testid={LANDING.waitlistSubmit} type="submit" className="btn-crisp btn-primary w-full justify-center">
              Reserve My Slot →
            </button>
            {status && (
              <div data-testid={LANDING.waitlistSuccess} className="text-sm saffron">{status}</div>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
